/**
 * The phone's position for a purchase, taken the way farmer-onboarding does it: WATCH the
 * position and keep the tightest fix, stop early once it is good enough, and always settle by a
 * hard deadline, so the screen can never hang waiting for GPS. The last good fix is kept for
 * when GPS is briefly unavailable.
 */

export interface GpsFix {
  lat: number
  lng: number
  /** Metres, as the phone reports it. */
  accuracy_m: number
  /** ISO time of the fix. */
  captured_at: string
  /** True when GPS failed now and the last good fix was used instead. */
  fallback?: boolean
}

export interface BestFixOptions {
  /** Stop as soon as a fix is this accurate (metres). */
  targetAccuracy?: number
  /** Give up after this long and keep the best fix so far. */
  maxWait?: number
  /** Each tighter fix, for the live "±N m" on screen. */
  onProgress?: (fix: GpsFix) => void
  /** Cancel (the screen closed). */
  signal?: AbortSignal
  geolocation?: Geolocation
}

export type GpsErrorCode = 'UNSUPPORTED' | 'DENIED' | 'NO_FIX' | 'CANCELLED'

export class GpsError extends Error {
  readonly code: GpsErrorCode
  constructor(message: string, code: GpsErrorCode) {
    super(message)
    this.name = 'GpsError'
    this.code = code
  }
}

const toFix = (pos: GeolocationPosition): GpsFix => ({
  lat: pos.coords.latitude,
  lng: pos.coords.longitude,
  accuracy_m: pos.coords.accuracy,
  captured_at: new Date(pos.timestamp || Date.now()).toISOString(),
})

export function getBestFix(opts: BestFixOptions = {}): Promise<GpsFix> {
  const targetAccuracy = opts.targetAccuracy ?? 20
  const maxWait = opts.maxWait ?? 20_000
  const geo =
    opts.geolocation ?? (typeof navigator !== 'undefined' ? navigator.geolocation : undefined)
  return new Promise((resolve, reject) => {
    if (!geo) {
      reject(new GpsError('This phone cannot give its location', 'UNSUPPORTED'))
      return
    }
    if (opts.signal?.aborted) {
      reject(new GpsError('cancelled', 'CANCELLED'))
      return
    }
    let best: GpsFix | null = null
    let done = false
    let watchId: number | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    const cleanup = () => {
      if (timer !== null) clearTimeout(timer)
      if (watchId !== null) {
        try {
          geo.clearWatch(watchId)
        } catch {
          // a watch that already ended
        }
      }
      opts.signal?.removeEventListener('abort', onAbort)
    }
    const settle = (fn: () => void) => {
      if (done) return
      done = true
      cleanup()
      fn()
    }
    const finish = () =>
      settle(() => {
        if (best) {
          saveLastFix(best)
          resolve(best)
        } else reject(new GpsError('No GPS fix. Go to open sky and try again', 'NO_FIX'))
      })
    const onAbort = () => settle(() => reject(new GpsError('cancelled', 'CANCELLED')))
    opts.signal?.addEventListener('abort', onAbort)
    // The deadline is set before the watch starts, so even a watch that never calls back ends.
    timer = setTimeout(finish, maxWait)
    try {
      watchId = geo.watchPosition(
        (pos) => {
          if (done) return
          const fix = toFix(pos)
          if (!best || fix.accuracy_m < best.accuracy_m) {
            best = fix
            opts.onProgress?.(fix)
          }
          if (fix.accuracy_m <= targetAccuracy) finish()
        },
        (err) => {
          // Permission refused never fixes itself by waiting: stop now.
          if (err.code === 1) {
            settle(() =>
              reject(new GpsError('Location is blocked. Allow location for this app', 'DENIED')),
            )
            return
          }
          // A timeout or a lost signal with a fix in hand: keep the best one.
          if (best) finish()
          // No fix yet: keep waiting for the deadline (the watch may still answer).
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: maxWait },
      )
    } catch {
      settle(() => reject(new GpsError('This phone cannot give its location', 'UNSUPPORTED')))
    }
  })
}

const LAST_FIX_KEY = 'uc.last_gps_fix'
/** A fallback older than this is not used: the operator is likely somewhere else by then. */
const LAST_FIX_MAX_AGE_MS = 12 * 60 * 60 * 1000

export function saveLastFix(fix: GpsFix): void {
  try {
    localStorage.setItem(LAST_FIX_KEY, JSON.stringify(fix))
  } catch {
    // private mode / storage full: only the fallback is lost
  }
}

export function getLastFix(now = Date.now()): GpsFix | null {
  try {
    const fix = JSON.parse(localStorage.getItem(LAST_FIX_KEY) ?? 'null') as GpsFix | null
    if (!fix || !Number.isFinite(fix.lat) || !Number.isFinite(fix.lng)) return null
    if (now - Date.parse(fix.captured_at) > LAST_FIX_MAX_AGE_MS) return null
    return fix
  } catch {
    return null
  }
}

/**
 * The best fix now, else the last good one (marked `fallback`) when GPS fails. Throws only
 * when there is neither: the caller shows the reason and a retry.
 */
export async function captureFix(opts: BestFixOptions = {}): Promise<GpsFix> {
  try {
    return await getBestFix(opts)
  } catch (e) {
    if (e instanceof GpsError && (e.code === 'CANCELLED' || e.code === 'DENIED')) throw e
    const last = getLastFix()
    if (last) return { ...last, fallback: true }
    throw e
  }
}
